import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import * as dayjs from 'dayjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DcAlertInnerService } from 'src/app/ui-kit/dc-alert-inner/dc-alert-inner.service';
import { DateUtils } from 'src/app/utils/date.utils';
import { WorkflowMessageContext } from 'src/app/workflows/workflow-message-handler/workflow-handler.const';
import { WorkflowMessageHandler } from 'src/app/workflows/workflow-message-handler/workflow-message-handler';
import { ProductTypeEnum } from '../../model/enums/product-capture.enums';
import { DropDownOption } from '../../model/interfaces/drop-down-option.interface';
import { ProductCaptureData } from '../../model/interfaces/product-capture.interface';
import { AnalyticsService } from '../../services/analytics.service';
import { AnalyticsTablesBaseComponent } from './analytics-tables-base/analytics-tables-base.component';
import { ANALYTICS_REPORTS_TYPES, AnalyticsReportsTypes } from './analytics.conts';
import {
  ANALYTICS_SOURCE,
  AnalyticsData
} from './analytics.interface';
import { PriipsTablesComponent } from './priips-tables/priips-tables.component';
import { StressTestingTablesComponent } from './stress-testing-tables/stress-testing-tables.component';

@Component({
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.scss'],
})
export class AnalyticsComponent extends WorkflowMessageHandler implements OnInit, OnDestroy {

  @Input() 
  productCaptureDetail: ProductCaptureData;

  loading = true;
  
  fvcAsOfDefault: NgbDateStruct;

  productType: string | null;
  
  tenor: string;

  source: ANALYTICS_SOURCE;

  disableRequestButton: boolean = false;

  private destroy$ = new Subject<void>();

  data: AnalyticsData[] = [];

  reportsTypes: DropDownOption[] = ANALYTICS_REPORTS_TYPES;

  selectedReportType: DropDownOption = this.reportsTypes[0];

  @ViewChild(PriipsTablesComponent)
  priipsTablesComponent: PriipsTablesComponent;

  @ViewChild(StressTestingTablesComponent)
  stressTestingTablesComponent: StressTestingTablesComponent;

  analyticsService = inject(AnalyticsService);

  dcAlertInnerService = inject(DcAlertInnerService);

  @Output() requestAnalyticsTimestamp = new EventEmitter<Date>();

  ngOnInit(): void {
    this.productType = this.getProductType(this.productCaptureDetail);
    this.fvcAsOfDefault = this.getFvcAsOfDefault(this.productCaptureDetail);
    this.tenor = this.getTenor(this.productCaptureDetail);
    this.source = this.productType === ProductTypeEnum.CLN ? ANALYTICS_SOURCE.CLOUD_RISK : ANALYTICS_SOURCE.FVC;

    this.fetchAnalytics();
  }

  fetchAnalytics(): void {
    this.loading = true;

    this.analyticsService
      .fetchAnalytics(`${this.productId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: AnalyticsData[]) => {
          this.loading = false;
          this.data = data;
        },
        (error) => {
          this.loading = false;
          this.dcAlertInnerService.showAlert({
            type: 'danger',
            message: `Error: ${error.statusText}`,
          });
        }
      );
  }

  logTimeOfAnalyticsRequest() {
    const dateTime = new Date();
    this.requestAnalyticsTimestamp.emit(dateTime);
  }

  getFvcAsOfDefault(productData: any): NgbDateStruct {
    const yesterday = DateUtils.getPreviousWeekdayAsNgbDate();

    if (this.productType === ProductTypeEnum.CLN) {
      return yesterday;
    }

    let initialObservationDateObj: NgbDateStruct =
      productData?.summary?.keyDates?.initialObservationDate?.value;

    if (initialObservationDateObj) {
      const today = DateUtils.convertDayJsDateToNgbDate(dayjs());
      const initialObsDate = DateUtils.convertNgbDateToDayJs(
        initialObservationDateObj
      );

      if (DateUtils.compareDates(initialObservationDateObj, today) < 0) {
        if (DateUtils.isWeekend(initialObservationDateObj)) {
          initialObservationDateObj =
            DateUtils.getPreviousWeekdayAsNgbDate(initialObsDate);
        }
      } else {
        initialObservationDateObj = yesterday;
      }
    }

    return !initialObservationDateObj ? yesterday : initialObservationDateObj;
  }

  getProductType(productData: any): string | null {
    return productData?.summary?.productType ?? null;
  }

  getTenor(productData: any): string {
    return productData?.summary?.tenor;
  }

  selectReportType(reportType: DropDownOption): void {
    this.selectedReportType = reportType;
  }

  get activeReport(): AnalyticsTablesBaseComponent {
    return this.selectedReportType.id === AnalyticsReportsTypes.PRIIPS
      ? this.priipsTablesComponent
      : this.stressTestingTablesComponent;
  }

  refreshData(): void {
    this.fetchAnalytics();
  }

  get context(): WorkflowMessageContext {
    return WorkflowMessageContext.ANALYTICS;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
